import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
// FIXME: Replace image
import replaceMe from 'assets/tmpImg.gif'
import { useChannel } from 'ducks/channels/hooks'
import { doSendVerification } from 'ducks/mailboxes/actions'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK } from 'ducks/drawers/types'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { styles } from './styles'
import { drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const ForwardingTest = ({
  drawerResourceId: channelId,
  drawerChannelType: channelType,
  previousDrawer,
  onClose,
}) => {
  const dispatch = useDispatch()
  const [heading, setHeading] = useState('Sending test mail')
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [isVerificationSent, setIsVerificationSent] = useState(false)
  const timers = useRef({
    firstStage: null,
    nextStage: null,
    timeoutStage: null,
    loadChannel: null,
  })

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_CREATION_FEEDBACK,
    closeIgnoresStack: true,
  })

  useEffect(() => {
    timers.current.firstStage = setTimeout(() => {
      setHeading('Just a little while longer')
    }, 20 * 1000)

    timers.current.nextStage = setTimeout(() => {
      setHeading('Hang in there')
    }, 40 * 1000)

    timers.current.timeoutStage = setTimeout(() => {
      setIsTimedOut(true)
    }, 60 * 1000)

    return () => {
      clearTimeout(timers.current.firstStage)
      clearTimeout(timers.current.nextStage)
      clearTimeout(timers.current.timeoutStage)
      clearTimeout(timers.current.loadChannel)
      timers.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    () => {
      if (isTimedOut && previousDrawer?.drawerId) {
        onClose({
          query: {
            ...buildDrawerQueryParam(
              previousDrawer.drawerId,
              'drawerVerificationFailed',
              1
            ),
          },
        })
      }
    },
    [isTimedOut, onClose, previousDrawer?.drawerId]
  )

  const {
    channel: { state } = {},
    isMissing,
    isLoading,
    loadChannel,
  } = useChannel(channelId, {
    skipLoaded: false,
  })

  const isConfirmed = ['active'].includes(state)

  useEffect(
    () => {
      if (isMissing) onClose()
    },
    [isMissing, onClose]
  )

  useEffect(
    () => {
      if (
        isConfirmed ||
        // We intentially only load on false value because
        // the built in effect inside useChannel will load
        // the channel will the value is null
        isLoading === false ||
        isMissing ||
        timers.current.loadChannel
      )
        return

      timers.current.loadChannel = setTimeout(() => {
        timers.current.loadChannel = null
        loadChannel()
      }, 5000)
    },
    [isLoading, isMissing, loadChannel, isConfirmed]
  )

  useEffect(
    () => {
      if (isConfirmed || isVerificationSent || isLoading !== false) return
      setIsVerificationSent(true)
      dispatch(doSendVerification(channelId)).catch(error => {
        // eslint-disable-next-line no-console
        console.error('Unable to send the verification email', { error })
        if (bugsnagClient) {
          bugsnagClient.notify(error, {
            severity: 'error',
            metaData: {
              channelId,
            },
          })
        }
      })
    },
    [dispatch, isConfirmed, isVerificationSent, isLoading, channelId]
  )

  useEffect(
    () => {
      if (!isConfirmed) return

      openNextDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            nextDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [isConfirmed, openNextDrawer, nextDrawerId, channelType, channelId]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const footer = (
    <ModalBtns tertiaryBtnText="Back" onClickTertiaryBtn={handleBack} />
  )

  const intro = (
    <>
      We’ve just sent a test email to make sure your forwarding is working
      correctly.
      <br />
      <br />
      This process normally takes a minute to receive the email and verify it’s
      working correctly.
    </>
  )

  const animatedHeading = (
    <>
      {heading}
      <AnimatedEllipsis />
    </>
  )

  return (
    <Drawer title={DEFAULT_DRAWER_TITLE} footer={footer} onClose={onClose}>
      {/* TODO: change image */}
      <HeaderWithImage
        className="grui mt-5 mb-12"
        imgWidth="136px"
        img={replaceMe}
        css={styles.header}
        alt="Email forwarding"
        title={animatedHeading}
        intro={intro}
      />
    </Drawer>
  )
}

export default ForwardingTest
